import {Injectable} from "@angular/core";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {Subject} from "rxjs";
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Globals} from "../../../core/services/globals";
import {DomSanitizer} from "@angular/platform-browser";
import {ViewportScroller} from "@angular/common";
import {TrackingService} from "../../../core/services/tracking.service";
import {NavbarBehaviorService} from "../../../core/services/navbar-behavior.service";
import {SEOService} from '../../../core/services/seo.service';
import {Data, Listing} from '../../../core/models/listing';
import {switchMap, takeUntil} from "rxjs/operators";
import {ObservableInput} from "rxjs/internal/types";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {ListingsService} from "../../../core/services/listings-service.interface";

@Injectable()
export abstract class BaseListingDetailsComponent {
  selectedListing: Listing | undefined;
  loadedBln = false;
  currentScreenSize: string | undefined;
  protected destroyed$ = new Subject<void>(); // For cleanup
  bsModalRef: BsModalRef | undefined;

  // Responsive display map for shared behavior
  protected displayNameMap = new Map([
    [Breakpoints.XSmall, 'XSmall'],
    [Breakpoints.Small, 'Small'],
    [Breakpoints.Medium, 'Medium'],
    [Breakpoints.Large, 'Large'],
    [Breakpoints.XLarge, 'XLarge'],
  ]);
  protected trackingURLStr = '/classes/REP_RETSTracking.php'; // URL to web api


  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected http: HttpClient,
    protected listingsService: ListingsService,
    protected SEOService: SEOService,
    public globals: Globals,
    protected _sanitizer: DomSanitizer,
    protected scroller: ViewportScroller,
    protected trackingService: TrackingService,
    protected breakpointObserver: BreakpointObserver,
    protected navbarService: NavbarBehaviorService,
    protected modalService: BsModalService,
  ) {
  }

  /**
   * Initialize dashboard base functionality - should be called in child components' ngOnInit
   */
  protected initializeBaseListingDetails(): void {
    // Apply navbar adjustments
    this.setupNavbar();

    // Detect screen changes
    this.setupBreakpointObserver();

    // fetch the listing
    this.fetchlisting();
  }

  /**
   * Clean up base dashboard resources - should be called in child components' ngOnDestroy
   */
  protected cleanupBaseDashboard(): void {
    // Clean up subscriptions
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Setup the navbar with proper spacing and styling
   */
  protected setupNavbar(): void {
    // initialize the navbar service
    this.navbarService.initialize();

    // Manually call updateHeaderOnScroll once to set the initial state
    this.navbarService.updateHeaderOnScroll();
  }


  // Common shared methods that can be used by both dashboards

  /**
   * Shared Breakpoint Observer Setup: Detect screen sizes and handle shared logic
   */
  protected setupBreakpointObserver(): void {
    this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        for (const query of Object.keys(result.breakpoints)) {
          if (result.breakpoints[query]) {
            this.currentScreenSize =
              this.displayNameMap.get(query) ?? 'Unknown';
          }
        }
        console.log('Screen size updated:', this.currentScreenSize);
      });
  }


  protected fetchlisting(): void {
    // console.log('detail indirect');
    // fork, if this is a non etobicoke search then use the peel halton db to search for the listing
    // fork, if an actual mls number has been defined in the route then get the listing
    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap): ObservableInput<Data> => {
            const MLSNum = params.get('MLSNum') || '';
            return this.listingsService.getListing(MLSNum);
          }
        ),
      )
      .subscribe((response: Data) => {
        this.selectedListing = this.fixImages(response.data[0] as Listing);
        this.loadedBln = true;
        // track the event
        this.trackEvent({_eventStr: 'view-listing'});
        this.trackingService.trackUserJourney(this.selectedListing.id, 'view', 'Client Viewed Listing');
        // set the seo data
        this.setSEOTags();

        this.onListingFetched(this.selectedListing);
      });
  }

  public trackEvent(obj: { _eventStr: string }) {
    return;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded;',
      'X-Requested-With': 'XMLHttpRequest',
    });
    const options = {
      headers: headers,
      params: this.generateEventTrackingData({obj: obj}),
    };
    const paramsObj = this.generateEventTrackingData({obj: obj});
    const params = new HttpParams({
      fromObject: paramsObj,
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };
    // track the event
    return this.http
      .post<Listing[]>(this.trackingURLStr, params.toString(), httpOptions)
      .subscribe(
        (response) => {
          // console.log(response.json());
        },
        (error) => {
          this.handleError(error); // Centralized error handling
        },
      );
  }

  private generateEventTrackingData({obj}: { obj: any }): any {
    // define properties
    const emailStr = this.globals.emailStr;
    const userIDStr = this.globals.userIDStr;
    const ipAddrStr = this.globals.ipAddrStr;
    const sidStr = this.globals.sidStr;
    const idStr = this.globals.idStr;
    const eventStr = obj._eventStr;
    const eventFullStr = this.generateEventFullString();
    // define properties
    const JSONObj = {
      idStr: idStr,
      emailStr: '',
      userIDStr: '',
      ipAddrStr: ipAddrStr,
      sessionIDStr: '',
      eventStr: eventStr,
      eventFullStr: eventFullStr,
      viewStr: 'list-view',
    };
    if (emailStr != '') {
      JSONObj.emailStr = emailStr;
    } else {
      delete (JSONObj as any)['emailStr'];
    }
    if (userIDStr != '') {
      JSONObj.userIDStr = userIDStr;
    } else {
      delete (JSONObj as any)['userIDStr'];
    }
    if (sidStr != '') {
      JSONObj.sessionIDStr = sidStr;
    } else {
      delete (JSONObj as any)['sessionIDStr'];
    }
    return JSONObj;
  }

  private generateEventFullString() {
    // define properties
    if (this.selectedListing == undefined) {
      return;
    }
    const MLSNum = this.selectedListing.MLSNUM;
    const propertyTypeStr = this.globals.propertyTypeStr;
    const eventFullObj = {
      MLSNum: MLSNum,
      propertyTypeStr: propertyTypeStr,
    };
    // return the json object as a string
    return JSON.stringify(eventFullObj);
  }

  private fixImages(listingDetailsCls: Listing) {
    // define properties
    const imageArr = listingDetailsCls.listingMedia.data;
    // iterate through the image array to fix the image URL's to the new serverless image resize solution
    for (let i = 0; i < imageArr.length; i++) {
      // define properties
      let urlStr = imageArr[i].url;
      // change the url to the new one
      urlStr = urlStr.replace(
        'https://d3hnn994gxsthv.cloudfront.net/800x800/',
        'https://d3hnn994gxsthv.cloudfront.net/fit-in/4000x4000/',
      );
      // update the array item
      listingDetailsCls.listingMedia.data[i].url = urlStr;
    }
    // return the listing details class
    return listingDetailsCls;
  }

  protected fixThumbnailImage(urlStr: string) {
    // define properties
    if (urlStr == undefined || urlStr == '') {
      return '';
    }
    // change the url to the new one
    urlStr = urlStr.replace(
      'https://d3hnn994gxsthv.cloudfront.net/fit-in/4000x4000/',
      'https://d3hnn994gxsthv.cloudfront.net/fit-in/380x219/',
    );
    // return the fixed url
    return urlStr;
  }

  protected setSEOTags() {
    // define properties
    const listingDetailsCls = this.selectedListing;
    if (listingDetailsCls == undefined) {
      return;
    }
    const addressStr = listingDetailsCls.listingDetail?.address ?? listingDetailsCls.fullAddress;
    const addressHyphenStr = addressStr.replaceAll(' ', '-');
    const cityStr = listingDetailsCls.municipality;
    const cityHypenStr = listingDetailsCls.municipality.replaceAll(' ', '-');
    const stateStr = 'ON';
    const MLSNum = listingDetailsCls.MLSNUM;
    const titleStr =
      addressStr +
      ', ' +
      cityStr +
      ', ' +
      stateStr +
      ' ' +
      ' (MLS #' +
      MLSNum +
      ') :: Cayman Marshall International Realty Inc., Brokerage';
    const descriptionStr =
      'Residential property for sale in ' +
      cityStr +
      ', ' +
      stateStr +
      ' (MLS #' +
      MLSNum +
      '). Learn more from Cayman Marshall.';
    const keywordsStr =
      'Real Estate, Houses, ' +
      addressStr +
      ', ' +
      cityStr +
      ', ' +
      stateStr +
      ', Residential, Full Service, $/Sf/Month, Yes, No';
    // set the SEO data
    this.SEOService.setMetaTags({
      _titleStr: titleStr,
      _descriptionStr: descriptionStr,
      _urlStr:
        'https://www.' +
        this.globals.hostnameStr +
        '/search/fulldetails/' +
        addressHyphenStr +
        '/' +
        cityHypenStr +
        '/' +
        MLSNum,
      _keywordsStr: keywordsStr,
    });
  }

  protected getBackground(image: string) {
    console.log('fixing background image');
    // deine properties
    const urlStr = image.replace(
      'https://d3hnn994gxsthv.cloudfront.net/fit-in/4000x4000/',
      'https://d3hnn994gxsthv.cloudfront.net/fit-in/2000x2000/',
    );
    return this._sanitizer.bypassSecurityTrustStyle(`url(${urlStr})`);
  }

  protected getBackgroundSmall(image: string) {
    console.log('fixing background image');
    // deine properties
    const urlStr = image.replace(
      'https://d3hnn994gxsthv.cloudfront.net/fit-in/4000x4000/',
      'https://d3hnn994gxsthv.cloudfront.net/fit-in/800x800/',
    );
    return this._sanitizer.bypassSecurityTrustStyle(`url(${urlStr})`);
  }

  protected gotoHashtag(fragment: string) {
    // console.log(fragment);
    this.scroller.scrollToAnchor(fragment);
    // el.scrollIntoView({behavior: 'smooth'});
  }


  protected handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }

  /**
   * Abstract method: Child classes can handle listings after they are fetched
   * @param listings Fetched listings array
   */
  protected abstract onListingFetched(listings: Listing): void;

}
